import BrainSearchInput from "@/brain-search/internal/BrainSearchInput"
import BrainSearchMessageList, {
  BrainSearchMessageListSkeleton,
} from "@/brain-search/internal/BrainSearchMessageList"
import BrainSearchPageHeader, {
  BrainSearchPageHeaderSkeleton,
} from "@/brain-search/internal/BrainSearchPageHeader"
import { BrainSearchPageContentFragment$key } from "@/brain-search/internal/__generated__/BrainSearchPageContentFragment.graphql"
import { useBrainSearchWidth } from "@/brain-search/internal/utils/BrainSearchUtils"
import AiApi from "@/common/AiApi"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import RestfulUtil from "@/core/restful/RestfulUtil"
import RelayEnvironment from "@/relay/RelayEnvironment"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import { displayRestfulErrorToast } from "@components/toast/ToastProvider"
import { DiscoInputSkeleton } from "@disco-ui"
import classNames from "classnames"
import { observer } from "mobx-react-lite"
import { useEffect, useRef, useState } from "react"
import { useFragment } from "react-relay"
import { default as ConnectionHandlerPlus } from "relay-connection-handler-plus"
import { RecordSourceProxy, commitLocalUpdate, graphql } from "relay-runtime"
import { BrainSearchPageContent__createMessageMutation } from "./__generated__/BrainSearchPageContent__createMessageMutation.graphql"
import { useIsMobile } from "@utils/hook/screenSizeHooks"

interface BrainSearchPageContentProps {
  brainSearchKey: BrainSearchPageContentFragment$key
}

function BrainSearchPageContent({ brainSearchKey }: BrainSearchPageContentProps) {
  const activeOrganization = useActiveOrganization()
  const drawer = useGlobalDrawer("brainSearch")
  const { isWide } = useBrainSearchWidth()
  const classes = useStyles({ isWide })
  const { prompt: initialPrompt } = drawer.params
  const abortControllerRef = useRef<AbortController>(new AbortController())
  const [generatingMessageId, setGeneratingMessageId] = useState<GlobalID | null>(null)
  const [isBotThinking, setIsBotThinking] = useState(false)
  const [messageInternalSourcesStatus, setMessageInternalSourcesStatus] = useState<
    Record<GlobalID, boolean>
  >({})

  useEffect(() => {
    if (!initialPrompt) return

    const { relatedQuestionId } = drawer.params
    handleCreateMessage(decodeURIComponent(initialPrompt), relatedQuestionId)
    drawer.setParams({ prompt: undefined, relatedQuestionId: undefined }, "replace")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt])

  const brainSearch = useFragment<BrainSearchPageContentFragment$key>(
    graphql`
      fragment BrainSearchPageContentFragment on BrainSearch
      @argumentDefinitions(first: { type: "Int!" }, after: { type: "String" }) {
        id
        ...BrainSearchPageHeaderFragment
        ...BrainSearchMessageList_PaginationFragment
          @arguments(first: $first, after: $after)
        contentUsage {
          ...BrainSearchInput_ContentUsageFragment
        }
      }
    `,
    brainSearchKey
  )

  const commit = Relay.useAsyncMutation<BrainSearchPageContent__createMessageMutation>(
    graphql`
      mutation BrainSearchPageContent__createMessageMutation(
        $id: ID!
        $userText: String!
        $sourceRelatedQuestionId: ID
      ) {
        createBrainSearchMessage(
          brainSearchId: $id
          userText: $userText
          sourceRelatedQuestionId: $sourceRelatedQuestionId
        ) {
          userMessage {
            id
            messageText
            type
            brainSearchId
          }
          assistantMessage {
            id
            messageText
            type
            sources {
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }
    `
  )

  return (
    <div className={classNames(classes.root, classes.fadeIn)}>
      <div className={classes.container}>
        {/* Header */}
        <BrainSearchPageHeader brainSearchKey={brainSearch} />

        <div className={classes.contentContainer}>
          {/* Messages */}
          <BrainSearchMessageList
            brainSearchKey={brainSearch}
            isBotThinking={isBotThinking}
            generatingMessageId={generatingMessageId}
            onCreateMessage={handleCreateMessage}
            messageInternalSourcesStatus={messageInternalSourcesStatus}
            // Add extra bottom padding when wide and the input is absolutely positioned
            paddingBottom={isWide ? 12 : undefined}
          />

          {/* Input */}
          <div className={classes.inputContainer}>
            <BrainSearchInput
              variant={isWide ? "raised" : "flat"}
              placeholder={"Ask a follow-up question..."}
              onSubmit={(prompt) => handleCreateMessage(prompt)}
              isGenerating={generatingMessageId !== null}
              onAbort={handleAbort}
              // Only show the source chip in the input when in side panel or mobile
              contentUsageKey={isWide ? undefined : brainSearch.contentUsage}
            />
          </div>
        </div>
      </div>
    </div>
  )

  function resetThinkingState() {
    setGeneratingMessageId(null)
    setIsBotThinking(false)
    setMessageInternalSourcesStatus({})
    abortControllerRef.current = new AbortController()
  }

  function handleAbort() {
    abortControllerRef.current.abort()
    resetThinkingState()
  }

  function getMessagesConnection(store: RecordSourceProxy, brainSearchId: GlobalID) {
    const brainSearchRecord = store.get(brainSearchId)
    if (!brainSearchRecord) return

    const messagesConnections = ConnectionHandlerPlus.getConnections(
      brainSearchRecord,
      "BrainSearchMessageList__messages"
    )
    if (!messagesConnections.length) return
    return messagesConnections[0]
  }

  async function handleCreateMessage(
    prompt: string,
    sourceRelatedQuestionId?: GlobalID | null
  ) {
    if (!activeOrganization) return

    const { userMessage, assistantMessage } = await createInitialMessages(
      prompt,
      sourceRelatedQuestionId
    )

    // Update the related question in the store if this message was created from one
    if (sourceRelatedQuestionId) {
      commitLocalUpdate(RelayEnvironment, (store) => {
        const questionRecord = store.get(sourceRelatedQuestionId)
        if (questionRecord) {
          const userMessageRecord = store.get(userMessage.id)
          if (userMessageRecord) {
            questionRecord.setLinkedRecord(userMessageRecord, "viewerBrainSearchMessage")
          }
        }
      })
    }
    // Generate the response
    try {
      const { response } = await AiApi.generateBrainSearchResponse(
        {
          messageText: prompt,
          brainSearchId: brainSearch.id,
          assistantMessageId: assistantMessage.id,
        },
        abortControllerRef.current
      )

      // Handle any errors
      const hasError = RestfulUtil.hasError(response)
      if (hasError) {
        await displayRestfulErrorToast(response)
        return
      }
      if (!response.body) return

      // Handle the text response stream
      setIsBotThinking(false)
      await handleBrainSearchResponse(response.body, assistantMessage.id)
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        resetThinkingState()
      } else {
        throw error
      }
    }

    // Invalidate the message so it's sources get refetched
    commitLocalUpdate(RelayEnvironment, (store) => {
      // Get the record for the assistant message
      const assistantMessageRecord = store.get(assistantMessage.id)
      if (!assistantMessageRecord) return

      assistantMessageRecord.invalidateRecord()

      // Also invalidate the BrainSearch record so the title gets refetched
      // This is important for the first response where the backend generates a new title
      // Only do this for the first response (when there are exactly 2 messages: 1 user + 1 assistant)
      const messagesConnection = getMessagesConnection(store, brainSearch.id)
      if (messagesConnection) {
        const messageCount = messagesConnection.getValue("totalCount")
        if (messageCount === 2) {
          const brainSearchRecord = store.get(brainSearch.id)
          if (brainSearchRecord) {
            brainSearchRecord.invalidateRecord()
          }
        }
      }
    })

    setGeneratingMessageId(null)
  }

  async function createInitialMessages(
    prompt: string,
    sourceRelatedQuestionId?: GlobalID | null
  ) {
    const { createBrainSearchMessage } = await commit({
      id: brainSearch.id,
      userText: prompt,
      sourceRelatedQuestionId: sourceRelatedQuestionId ?? undefined,
    })
    const { userMessage, assistantMessage } = createBrainSearchMessage

    setIsBotThinking(true)
    setGeneratingMessageId(assistantMessage.id)

    commitLocalUpdate(RelayEnvironment, (store) => {
      const messagesConnection = getMessagesConnection(store, brainSearch.id)
      if (!messagesConnection) return

      // Create a tmp user message record in the Relay Store
      const tmpUserMessage = Relay.fabricateNode(store, "BrainSearchMessage", {
        id: Relay.fromGlobalId(userMessage.id).id,
        messageText: prompt,
        type: "user",
        brainSearchId: brainSearch.id,
        sourceBrainSearchRelatedQuestionId: sourceRelatedQuestionId
          ? Relay.fromGlobalId(sourceRelatedQuestionId).id
          : null,
      })
      Relay.insertNodeIntoPaginatedConnection(store, messagesConnection, tmpUserMessage)

      // Create a tmp assistant message record in the Relay Store
      const tmpAssistantMessage = Relay.fabricateNode(store, "BrainSearchMessage", {
        id: Relay.fromGlobalId(assistantMessage.id).id,
        messageText: "",
        type: "assistant",
      })
      Relay.insertNodeIntoPaginatedConnection(
        store,
        messagesConnection,
        tmpAssistantMessage
      )
    })

    return { userMessage, assistantMessage }
  }

  async function handleBrainSearchResponse(
    responseBody: ReadableStream<Uint8Array>,
    assistantMessageId: GlobalID
  ) {
    await RestfulUtil.handleStream(
      responseBody,
      (decodedChunk) => {
        if (isBotThinking) setIsBotThinking(false) // Set this to false as soon as it starts typing

        commitLocalUpdate(RelayEnvironment, (store) => {
          const tmpAssistantMessageRecord = store.get(assistantMessageId)
          if (!tmpAssistantMessageRecord) return

          const existingText = tmpAssistantMessageRecord
            .getValue("messageText")
            ?.toString()
          Relay.deepUpdate(store, tmpAssistantMessageRecord, {
            messageText: existingText + decodedChunk,
          })
        })

        return true
      },
      {
        abortController: abortControllerRef.current,
        dataHandlers: {
          onStart: (data: any) => {
            // Handle initial data sent with startStream
            if (data.hasInternalSources !== undefined) {
              setMessageInternalSourcesStatus((prev) => ({
                ...prev,
                [assistantMessageId]: data.hasInternalSources,
              }))
            }
          },
        },
      }
    )
  }
}

type StyleProps = {
  isWide: boolean
}

const useStyles = makeUseStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    overflow: "hidden",
  },
  contentContainer: {
    display: "grid",
    gridTemplateRows: "1fr",
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  inputContainer: ({ isWide }: StyleProps) => ({
    display: "flex",
    padding: isWide ? theme.spacing(1.5, 1.5, 3) : theme.spacing(2),
    flexDirection: "column",
    alignItems: "center",
    ...styleIf(isWide, {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1,
    }),
    ...styleIf(!isWide, {
      borderTop: theme.palette.constants.borderSmall,
    }),
  }),
  fadeIn: {
    animation: "$fadeIn 1s",
  },
  // eslint-disable-next-line local-rules/disco-unused-classes
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
}))

export function BrainSearchPageContentSkeleton() {
  const isMobile = useIsMobile()
  const drawer = useGlobalDrawer("brainSearch")
  const isSidePanel = Boolean(drawer.params.drawerAsk)
  const isWide = !isMobile && !isSidePanel
  const classes = useStyles({ isWide })

  return (
    <div className={classNames(classes.root, classes.fadeIn)}>
      <div className={classes.container}>
        {/* Header Skeleton */}
        <BrainSearchPageHeaderSkeleton />

        <div className={classes.contentContainer}>
          {/* Messages Skeleton */}
          <BrainSearchMessageListSkeleton paddingBottom={isWide ? 12 : undefined} />

          {/* Input Skeleton */}
          <div className={classes.inputContainer}>
            <DiscoInputSkeleton
              height={40}
              style={{ width: "100%", maxWidth: "720px" }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Relay.withSkeleton({
  component: observer(BrainSearchPageContent),
  skeleton: BrainSearchPageContentSkeleton,
})
